import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { BellRing, Volume2, VolumeX } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import api from '@/lib/api';
import { createReminderController } from '@/lib/service-reminder-controller.mjs';

let soundSession = null;
function soundReady(scope) {
  return soundSession?.scope === scope && soundSession.armed && soundSession.context.state === 'running';
}
async function unlock(scope) {
  const Constructor = window.AudioContext || window.webkitAudioContext;
  if (!Constructor) throw Error('sound_unavailable');
  if (soundSession?.scope !== scope) {
    soundSession?.context.close().catch(() => {});
    soundSession = { scope, context: new Constructor(), armed: false };
  }
  const session = soundSession;
  let timer;
  try {
    await Promise.race([session.context.resume(), new Promise((_, reject) => { timer = setTimeout(() => reject(Error('sound_timeout')), 5000); })]);
    if (soundSession !== session || session.context.state !== 'running') throw Error('sound_blocked');
    session.armed = true;
  } finally { clearTimeout(timer); }
}
function play(scope) {
  if (!soundReady(scope)) return false;
  const context = soundSession.context;
  [880, 1108, 880].forEach((frequency, index) => {
    const at = context.currentTime + index * 0.23;
    const oscillator = context.createOscillator();
    const gain = context.createGain();
    oscillator.frequency.value = frequency;
    gain.gain.setValueAtTime(0, at);
    gain.gain.linearRampToValueAtTime(0.1, at + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.001, at + 0.2);
    oscillator.connect(gain); gain.connect(context.destination);
    oscillator.onended = () => { oscillator.disconnect(); gain.disconnect(); };
    oscillator.start(at); oscillator.stop(at + 0.21);
  });
  return true;
}

export default function OperationalServiceReminder() {
  const { user } = useAuth();
  if (!user?.id || user.profile_test || !['super_admin', 'admin', 'dispatcher', 'customer_service'].includes(user.role)) return null;
  const scope = JSON.stringify([user.id, user.role, user.company_id]);
  return <Reminder key={scope} scope={scope} canConfigure={['super_admin', 'admin'].includes(user.role)} />;
}

function Reminder({ scope, canConfigure }) {
  const [queue, setQueue] = useState(null);
  const [error, setError] = useState('');
  const [armed, setArmed] = useState(() => soundReady(scope));
  const [soundBusy, setSoundBusy] = useState(false);
  const [lastPlayed, setLastPlayed] = useState(null);
  const [preview, setPreview] = useState(false);
  const [acknowledging, setAcknowledging] = useState('');
  const previewUntil = useRef(0);
  const active = useRef(false);
  const generation = useRef(0);
  const refreshRef = useRef(null);
  const controller = useRef(null);
  const ackLock = useRef(false);

  useEffect(() => {
    active.current = true;
    let disposed = false;
    let flight = false;
    let dueTimer;
    const aborts = new Set();
    const player = createReminderController({
      storage: { getItem: key => window.localStorage.getItem(key), setItem: (key, value) => window.localStorage.setItem(key, value) },
      withLock: (name, action) => navigator.locks
        ? navigator.locks.request(name, { ifAvailable: true }, lock => lock ? action() : false)
        : action(),
      play: () => play(scope),
      onPlayed: at => {
        if (disposed) return;
        setLastPlayed(at); clearTimeout(dueTimer);
        dueTimer = setTimeout(refresh, 60000);
      },
    });
    controller.current = player;
    async function refresh() {
      if (disposed || flight) return;
      flight = true;
      const version = ++generation.current;
      const abort = new AbortController(); aborts.add(abort);
      try {
        const { data } = await api.get('/service-reminders/pending', { timeout: 10000, signal: abort.signal });
        if (disposed || version !== generation.current) return;
        if (!data || !Array.isArray(data.items) || typeof data.effective_enabled !== 'boolean' || data.interval_seconds !== 60) throw Error('invalid_reminders');
        setQueue(data); setError('');
        const testing = previewUntil.current > Date.now();
        if (!testing) { previewUntil.current = 0; setPreview(false); }
        const ready = soundReady(scope); setArmed(ready);
        await player.tick({ scope, enabled: !ackLock.current && (testing || (data.effective_enabled && data.sound_allowed)), armed: ready,
          items: testing ? [{ id: 'preview' }] : data.items });
      } catch {
        if (!disposed && version === generation.current) {
          player.invalidate(); setError('No se pudo actualizar la atención de las solicitudes. Reintentando…');
        }
      } finally { aborts.delete(abort); flight = false; }
    }
    refreshRef.current = refresh;
    refresh();
    const interval = setInterval(refresh, 10000);
    const changed = () => { generation.current += 1; player.invalidate(); refresh(); };
    window.addEventListener('ca-service-reminders-updated', changed);
    return () => {
      disposed = true; active.current = false; generation.current += 1;
      clearInterval(interval); clearTimeout(dueTimer); player.dispose(); aborts.forEach(abort => abort.abort());
      window.removeEventListener('ca-service-reminders-updated', changed);
      if (controller.current === player) controller.current = null;
      if (refreshRef.current === refresh) refreshRef.current = null;
    };
  }, [scope]);

  async function enableSound(test = false) {
    if (soundBusy) return;
    setSoundBusy(true); setError('');
    try {
      await unlock(scope);
      if (!active.current) return;
      setArmed(true);
      if (test) { previewUntil.current = Date.now() + 125000; setPreview(true); }
      refreshRef.current?.();
    } catch { if (active.current) setError('No se pudo activar el sonido. Pulsa de nuevo para intentarlo.'); }
    finally { if (active.current) setSoundBusy(false); }
  }
  function stopPreview() {
    previewUntil.current = 0; setPreview(false); controller.current?.invalidate(); refreshRef.current?.();
  }
  async function acknowledge(id) {
    if (ackLock.current || error) return;
    ackLock.current = true; setAcknowledging(id); generation.current += 1; controller.current?.invalidate();
    try {
      await api.post(`/services/${encodeURIComponent(id)}/acknowledge`, {}, { timeout: 10000 });
      if (!active.current) return;
      setQueue(value => value ? { ...value, items: value.items.filter(item => item.id !== id) } : value);
      window.dispatchEvent(new Event('ca-service-reminders-updated'));
    } catch { if (active.current) setError('No se pudo confirmar la atención. Actualiza el panel antes de repetir.'); }
    finally { ackLock.current = false; if (active.current) setAcknowledging(''); refreshRef.current?.(); }
  }
  if (!queue?.enabled) return error ? <p role="status" className="mx-4 mt-3 text-sm text-amber-300">{error}</p> : null;
  const pending = queue.items;
  return <section aria-label="Avisos de solicitudes sin atender" className={`m-4 rounded-lg border p-4 ${pending.length || preview ? 'border-red-500/60 bg-red-950/25' : 'border-neutral-800 bg-neutral-950'}`} data-testid="service-reminder-banner">
    <div className="flex flex-wrap gap-3 items-center">
      <BellRing size={19} className={pending.length || preview ? 'text-red-400' : 'text-neutral-400'} />
      <strong className="flex-1">{preview ? 'Prueba de aviso repetido' : pending.length ? `${pending.length}${queue.has_more ? '+' : ''} solicitud(es) sin atender` : queue.effective_enabled ? 'Avisos de solicitudes activos' : 'Avisos pausados por la configuración del canal'}</strong>
      <button type="button" disabled={soundBusy} onClick={() => armed ? (soundSession.armed = false, setArmed(false)) : enableSound()} className="border border-neutral-600 rounded px-3 py-2 text-sm min-h-11" data-testid="arm-reminder-sound">{armed ? <VolumeX size={14} className="inline mr-1" /> : <Volume2 size={14} className="inline mr-1" />}{armed ? 'Silenciar este panel' : 'Activar sonido'}</button>
      <button type="button" disabled={soundBusy} onClick={() => preview ? stopPreview() : enableSound(true)} className="underline text-sm min-h-11" data-testid="test-service-reminder">{preview ? 'Detener prueba' : 'Probar aviso'}</button>
      {canConfigure && <Link className="underline text-sm min-h-11 inline-flex items-center" to="/admin/settings/service-alerts">Configurar avisos</Link>}
    </div>
    <p className="text-xs text-neutral-400 mt-2">{preview ? 'Aviso de prueba. Finaliza automáticamente en dos minutos.' : 'El sonido se repite cada minuto con el panel abierto hasta que Operaciones atienda la solicitud.'}</p>
    {lastPlayed && <p className="text-xs text-neutral-400 mt-1" data-testid="last-reminder-played">Último aviso sonoro: {new Date(lastPlayed).toLocaleTimeString('es-DO')}</p>}
    {armed && !queue.sound_allowed && !preview && <p className="text-xs text-amber-300 mt-1">El sonido está pausado en la configuración de alertas.</p>}
    {error && <p role="alert" className="mt-2 text-amber-300 text-sm">{error}</p>}
    {!error && pending.slice(0, 3).map(item => <div key={item.id} className="mt-3 pt-3 border-t border-neutral-700 flex flex-wrap gap-3 items-center" data-testid={`reminder-${item.service_number}`}>
      <Link to={`/service/${encodeURIComponent(item.id)}`} className="flex-1 underline font-semibold">{item.service_number} · {item.client_name}</Link>
      <button type="button" disabled={!!acknowledging} onClick={() => acknowledge(item.id)} className="min-h-11 border border-emerald-500/50 rounded px-3 text-emerald-300" data-testid={`attend-${item.service_number}`}>{acknowledging === item.id ? 'Confirmando…' : 'Estoy atendiendo'}</button>
    </div>)}
    {pending.length > 3 && <Link to="/dispatch" className="block underline text-sm mt-3">Ver todas en despacho</Link>}
  </section>;
}
